import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getSchoolStatsRequest,
  getSchoolStudentStatsRequest,
  updateSchoolStatsRequest,
  updateSchoolStudentStatsRequest,
} from "../../../../Backend/SchoolsRequests";
import {
  displayDateTime,
  displayPercent,
  formatNumber,
  getPercent,
  getPropertyFromObject,
  objectToQueryString,
  parseDateFromUtc,
} from "../../../../Helpers/utils/utils";
import { SelectSchoolModal } from "./SelectSchoolModal/SelectSchoolModal";
import { WebRoutes } from "../../../../Constants";
import { getReportTypeIdByName } from "../../../../Constants/ReportTypes";
import "./SchoolStats.scss";

export function SchoolStats({
  loading,
  schoolList,
  schoolSelected,
  setSchoolSelected,
  setLoading,
}) {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [schoolBooksStats, setSchoolBooksStats] = useState([]);
  const [reportedBooksStats, setReportedBooksStats] = useState([]);
  const [studentStats, setStudentStats] = useState([]);
  const [studentLoginStats, setStudentLoginStats] = useState([]);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [bookStatsError, setBookStatsError] = useState(false);
  const [studentStatsError, setStudentStatsError] = useState(false);

  useEffect(() => {
    if (schoolSelected) {
      getStats();
    }
  }, [schoolSelected]);

  const getStats = async () => {
    setLoading(true);
    Promise.all([getSchoolStats(), getStudentsSchoolStats()]).then(() => {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    });
  };

  const getSchoolStats = async () => {
    const resp = await getSchoolStatsRequest(
      getPropertyFromObject(schoolSelected, "id")
    );
    if (resp.success) {
      setSchoolBooksStats(parseBookStats(resp.data));
      setReportedBooksStats(parseReportedBooks(resp.data));
      setBookStatsError(false);
    } else {
      setBookStatsError(true);
    }
  };

  const getStudentsSchoolStats = async () => {
    const resp = await getSchoolStudentStatsRequest(
      getPropertyFromObject(schoolSelected, "id")
    );
    if (resp.success) {
      setStudentStats(parseStudentStats(resp.data));
      setStudentLoginStats(parseStudentLoginStats(resp.data));
      setStudentStatsError(false);
    } else {
      setStudentStatsError(true);
    }
  };

  const updateAllStats = async () => {
    setUpdated(true);
    setLoading(true);
    Promise.all([
      updateSchoolStatsRequest(getPropertyFromObject(schoolSelected, "id")),
      updateSchoolStudentStatsRequest(
        getPropertyFromObject(schoolSelected, "id")
      ),
    ]).then(() => {
      getStats();
    });
  };

  const parseBookStats = (stats) => {
    return {
      percentInitialBooks: stats.percentInitialBooks,
      percentKeepedBooks: stats.percentKeepedBooks,
      percentReportedBooks: stats.percentReportedBooks,
      percentReturnedBooks: stats.percentReturnedBooks,
      totalBooks: stats.totalBooks,
      totalInitialBooks: stats.totalInitialBooks,
      totalKeepedBooks: stats.totalKeepedBooks,
      totalReportedBooks: stats.totalReportedBooks,
      totalReturnedBooks: stats.totalReturnedBooks,
      date: stats.date,
    };
  };

  const parseReportedBooks = (stats) => {
    return [
      {
        name: "ReportAlreadyReturned",
        value: stats.percentReportedByAlreadyTurnedBooks,
      },
      {
        name: "ReportGaveBookToTeacher",
        value: stats.percentReportedByGaveToTeacherBooks,
      },
      {
        name: "ReportLostBook",
        value: stats.percentReportedByLostBooks,
      },
      {
        name: "ReportBookNotReceived",
        value: stats.percentReportedByNeverRecivedBooks,
      },
    ];
  };

  const parseStudentLoginStats = (stats) => {
    return {
      users: stats.users,
      userLogged: stats.userLogged,
      userNotLogged: stats.userNotLogged,
      percentUserLogged: getPercent(stats.userLogged, stats.users),
      percentUserNotLogged: getPercent(stats.userNotLogged, stats.users),
    };
  };

  const parseStudentStats = (stats) => {
    return [
      {
        name: t("userWithAllInitial"),
        value: stats.userWithAllInitial,
        percent: getPercent(stats.userWithAllInitial, stats.users),
        color: "#ffffff",
      },
      {
        name: t("userWithAllNotInitial"),
        value: stats.userWithAllNotInitial,
        percent: getPercent(stats.userWithAllNotInitial, stats.users),
        color: "#13c3a3",
      },
      {
        name: t("userWithSomeKeeped"),
        value: stats.userWithSomeKeeped,
        percent: getPercent(stats.userWithSomeKeeped, stats.users),
        color: "#0562b0",
      },
      {
        name: t("userWithSomeNotInitial"),
        value: stats.userWithSomeNotInitial,
        percent: getPercent(stats.userWithSomeNotInitial, stats.users),
        color: "#0a84db",
      },
      {
        name: t("userWithSomeReported"),
        value: stats.userWithSomeReported,
        percent: getPercent(stats.userWithSomeReported, stats.users),
        color: "#f79249",
      },
    ];
  };

  return (
    <div className="school-stats trans-fromTop">
      <div className="header-wrapper">
        {schoolList.length > 0 && (
          <div
            className="select-school-wrapper"
            onClick={() => setShowSelectModal(true)}
          >
            <div className="select-school">
              <div className="school-number">{schoolSelected.schoolNumber}</div>
              <div className="school-name">{schoolSelected.name}</div>
            </div>
            <div className="icon">compare_arrows</div>
          </div>
        )}
        <div className="update-stats-wrapper">
          <div className="text">
            {t("LastUpdateDateTime")}:{" "}
            {displayDateTime(parseDateFromUtc(schoolBooksStats.date))}{" "}
          </div>
          {updated === false && (
            <div
              className="update-btn"
              title={t("UpdateStats")}
              onClick={() => updateAllStats()}
            >
              refresh
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <div className="loader-container">
          <div className="loader" />
        </div>
      ) : (
        <div className="chart-wrapper">
          {bookStatsError ? (
            <div className="column-wrapper">
              <div className="title">{t("Books")}</div>
              <div className="panel-wrapper-row">
                <div className="panel error">
                  <div className="icon">warning</div>
                  <div className="text">{t("ErrorLoadingStats")}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="column-wrapper">
              <div className="title">{t("Books")}</div>
              <div className="panel-wrapper-row">
                <div className="panel">
                  <div className="subtitle">{t("TotalBooks")}</div>
                  <div className="value">
                    {formatNumber(schoolBooksStats.totalBooks)}
                  </div>
                </div>
                <div className="panel">
                  <div className="subtitle">{t("StatusOfBooks")}</div>
                  <div className="stats-row">
                    <div className="stats-column initial">
                      <div className="static-value">
                        {formatNumber(schoolBooksStats.totalInitialBooks)}
                      </div>
                      <div className="hover-value">
                        {displayPercent(schoolBooksStats.percentInitialBooks)}
                      </div>
                      <div className="text">{t("Initial")}</div>
                    </div>
                    <div className="stats-column keeped">
                      <div className="static-value">
                        {formatNumber(schoolBooksStats.totalKeepedBooks)}
                      </div>
                      <div className="hover-value">
                        {displayPercent(schoolBooksStats.percentKeepedBooks)}
                      </div>
                      <div className="text">{t("Keeped")}</div>
                    </div>
                  </div>
                  <div className="stats-row">
                    <div className="stats-column returned">
                      <div className="static-value">
                        {formatNumber(schoolBooksStats.totalReturnedBooks)}
                      </div>
                      <div className="hover-value">
                        {displayPercent(schoolBooksStats.percentReturnedBooks)}
                      </div>
                      <div className="text">{t("Returned")}</div>
                    </div>
                    <div className="stats-column reported">
                      <div className="static-value">
                        {formatNumber(schoolBooksStats.totalReportedBooks)}
                      </div>
                      <div className="hover-value">
                        {displayPercent(schoolBooksStats.percentReportedBooks)}
                      </div>
                      <div className="text">{t("Reported")}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="chart">
                <div className="bar-chart">
                  <div className="subtitle">{t("ReportType")}</div>
                  {reportedBooksStats.map((item, index) => (
                    <div key={index} className="bar-container">
                      <div
                        className="label pointer"
                        onClick={() => {
                          const params = {
                            reportType: getReportTypeIdByName(item.name),
                            schoolId: schoolSelected.id,
                          };
                          navigate(
                            WebRoutes.StudentOverviewPage +
                              objectToQueryString(params, true)
                          );
                        }}
                      >
                        {t(item.name)}
                      </div>
                      <div className="bar-wrapper">
                        <div className="bar-full">
                          <div
                            className="bar"
                            style={{ width: `${item.value}%` }}
                          ></div>
                        </div>
                        <div className="bar-label">{item.value}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {studentStatsError ? (
            <div className="column-wrapper">
              <div className="title">{t("Students")}</div>
              <div className="panel-wrapper-row">
                <div className="panel error">
                  <div className="icon">warning</div>
                  <div className="text">{t("ErrorLoadingStats")}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="column-wrapper">
              <div className="title">{t("Students")}</div>
              <div className="panel-wrapper-row">
                <div className="panel">
                  <div className="subtitle">{t("TotalStudents")}</div>
                  <div className="value">
                    {formatNumber(studentLoginStats.users)}
                  </div>
                </div>
                <div className="panel">
                  <div className="subtitle">{t("LogInStatus")}</div>
                  <div className="stats-row">
                    <div className="stats-column not-logged">
                      <div className="static-value">
                        {formatNumber(studentLoginStats.userNotLogged)}
                      </div>
                      <div className="hover-value">
                        {displayPercent(studentLoginStats.percentUserNotLogged)}
                      </div>
                      <div className="text">{t("NotLogged")}</div>
                    </div>
                    <div className="stats-column logged">
                      <div className="static-value">
                        {formatNumber(studentLoginStats.userLogged)}
                      </div>
                      <div className="hover-value">
                        {displayPercent(studentLoginStats.percentUserLogged)}
                      </div>
                      <div className="text">{t("Logged")}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="chart">
                <div className="bar-chart">
                  <div className="subtitle">{t("StudentStatus")}</div>
                  {studentStats.map((item, index) => (
                    <div key={index} className="bar-container">
                      <div className="label">{item.name}</div>
                      <div className="bar-wrapper">
                        <div className="bar-full">
                          <div
                            className="bar"
                            style={{
                              width: `${item.percent}%`,
                            }}
                          ></div>
                        </div>
                        <div className="bar-label">
                          {`${item.percent}% (${item.value})`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {showSelectModal && (
        <SelectSchoolModal
          show={showSelectModal}
          schoolList={schoolList}
          schoolSelected={schoolSelected}
          setSchoolSelected={setSchoolSelected}
          onHide={() => setShowSelectModal(false)}
        />
      )}
    </div>
  );
}
